import { useAppContext } from '@/context';
import { logout, updateUserInStorage } from '@/utils/auth';
import { patchJson } from '@/utils/fetchOptions';
import { synchronizeLanguage, hasUrlLanguagePrefix } from '@/utils/languages';

import { useAuth, useAuthToken } from './useAuth';
import { useMutation } from './useMutation';

interface UseUserUpdateProps {
  want_email_notifications?: boolean;
  want_sms_notifications?: boolean;
  send_phone_verification_sms?: boolean;
  language?: Api.Language;
  first_name?: string;
  last_name?: string;
  country?: string;
  phone?: string;
  email?: string;
  tax_country?: string;
  shipping_address?: Api.ShippingAddress;
  onSuccess?: (res: ResponseObject | Api.UserObject) => void;
}

export function useUserUpdate({
  want_email_notifications,
  want_sms_notifications,
  send_phone_verification_sms,
  first_name,
  last_name,
  language,
  country,
  email,
  phone,
  tax_country,
  shipping_address,
  onSuccess
}: UseUserUpdateProps) {
  const { authData, setAuthData } = useAuth();
  const { setAlert } = useAppContext();
  const token = useAuthToken();

  const {
    isLoading,
    data = {},
    mutate,
    error
  } = useMutation({
    mutationKey: 'user-update',
    mutationFn: async () =>
      patchJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/user/update`,
        body: {
          want_email_notifications,
          want_sms_notifications,
          send_phone_verification_sms,
          first_name,
          last_name,
          language,
          country,
          email,
          phone,
          shipping_address,
          tax_country
        },
        token
      }).then((response: Api.User | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response) {
          setAlert(response.code);
          return {};
        }

        // Extract user object from response (Api.User has a 'user' property)
        const userObject = 'user' in response ? response.user : (response as Api.UserObject);
        updateUserInStorage(userObject);
        setAuthData({ ...authData, user: userObject });

        // Update language if user has a language preference and no URL language is specified
        if (!hasUrlLanguagePrefix() && userObject.language) {
          synchronizeLanguage(userObject.language, 'user-update');
        }

        return response;
      }),
    onSuccess
  });

  return {
    isLoading,
    mutate,
    error,
    data
  };
}
